/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { URLs } from "common/lib/constants";
import { router } from "common/shared";
import { AltCustodyAssetPages, ChartDataKey, ChartTitle } from "domains/altCustody/shared/constants";
import { AssetType } from "domains/liquidity/shared/constants";
import { IChart } from "domains/altCustody/shared/types";
import { currencyFormatter } from "domains/liquidity/shared/utils/inputValueFormatter";

/*
return {
  ...chart,
  data: Object.values(
    formattedChartData.reduce((acc: any, chart_: any) => {
      const key = chart_[ chart.dataKey ];
      const nav = parseFloat(chart_[ "investor_nav" ] || "0");

      if (acc[ key ]) {
        acc[ key ].count += 1;
        acc[ key ].total_nav += nav;
      } else {
        acc[ key ] = {
          name: key,
          count: 1,
          total_nav: nav,
        };
      }

      return acc;
    }, {}),
  ).map((item: any) => ({
    name: item.name,
    value: item.count,       // número de ocurrencias (como antes)
    total_nav: item.total_nav, // suma de los NAVs
  })),
};*/

export const prepareChartData = ({ data, dataKey }: { data: any; dataKey: `${ ChartDataKey }` }): IChart["data"] => {
  const formatData = (): IChart["data"] => {
    // Usamos un objeto con tipo explícito para el acumulador
    const result = data.reduce((acc: Record<string, { name: string; value: number; total_nav: number, isNegative: boolean }>, cur: any) => {
      const key = cur[ dataKey ];
      const nav = parseFloat(cur[ "investor_nav" ] || "0");
  
      if (!key) return acc;
  
      if (acc[ key ]) {
        acc[ key ].value += 1;
        acc[ key ].total_nav += nav;
      } else {
        acc[ key ] = {
          name: key,
          value: 1,
          total_nav: nav,
          isNegative: Number(nav) < 0,
        };
      }
  
      return acc;
    }, {});  // Inicializamos con un objeto vacío
  
    // Convertimos el objeto a un array de valores con los campos correctos

    const resultCharts = Object.values(result).map((item:any) => ({
      name: item.name,
      value: item.value,
      total_nav: Math.abs(item.total_nav),
      isNegative: Number(item.total_nav) < 0,
    }))
    return resultCharts;
  };
  
  const formatNumericData = (dataKey: string) => {
    type Accumulator = {
      [key: string]: {
        total: number;
        values: number[];
        originalValue: string; // Guardamos el valor original para formatear
      };
    };
  
    const reducedData = data.reduce((acc: Accumulator, cur: any) => {
      if (cur[ dataKey ]) {
        const originalValue = cur[ dataKey ];
        const numericValue = parseFloat(originalValue.replace(/[^0-9.-]/g, '')) || 0;
        const key = numericValue.toString(); // Agrupamos por valor numérico
  
        if (!acc[ key ]) {
          acc[ key ] = {
            total: 0,
            values: [],
            originalValue: originalValue,
          };
        }
  
        acc[ key ].total += numericValue;
        acc[ key ].values.push(numericValue);
      }
  
      return acc;
    }, {} as Accumulator);
  
    const charts = Object.entries(reducedData).map(([ numericValue, { total, values, originalValue } ]:any) => {
      const firstValueFormatted = currencyFormatter(values[ 0 ]);
      const showIndividual = values.length > 1;
      const isNegative = Number(total) < 0;
      return {
        name: showIndividual
          ? `Investor NAV (${ isNegative ? '- ' : '' }${ firstValueFormatted })`
          // ? `Investor NAV (${ firstValueFormatted } × ${ values.length })`
          : `Investor NAV`,
        value: total,
        total_nav: Math.abs(total),
        barValue: values[ 0 ],
        count: values.length, // Opcional: para saber cuántos items se agruparon
        isNegative,
      };
    }) as IChart["data"];
  
    return charts;
  };
  
  const formatDateData = (nameDataKey: string) =>
    Object.entries(
      data.reduce((acc: any, cur: any) => {
        acc[ cur[ nameDataKey ] ] = new Date(cur[ dataKey ]).getTime();

        return acc;
      }, {}),
    ).map(([ name, value ]) => ({
      name,
      value,
    })) as unknown as IChart["data"];

  switch (dataKey) {
    case ChartDataKey.INSTRUMENT_TYPE:
      return formatData();

    case ChartDataKey.FUND_INVESTMENT_NAV:
      return formatNumericData("investor_nav");

    case ChartDataKey.PUBLIC_OR_PRIVATE:
      return formatData();

    case ChartDataKey.COUNTRY:
      return formatData();

    case ChartDataKey.ASSET_CLASS:
      return formatData();

    case ChartDataKey.SUB_ASSET_CLASS:
      return formatData();

    case ChartDataKey.SECTOR:
      return formatData();

    case ChartDataKey.INDUSTRY_GROUP:
      return formatData();

    case ChartDataKey.INDUSTRY:
      return formatData();

    case ChartDataKey.SUB_INDUSTRY:
      return formatData();

    case ChartDataKey.NAV_AS_OF_DATE:
      return formatData();

    default:
      return {} as IChart["data"];
  }
};

export const formatChartTitleToHumanReadable = (dataKey: `${ ChartDataKey }`) => {
  switch (dataKey) {
    case ChartDataKey.INSTRUMENT_TYPE:
      return ChartTitle.INSTRUMENT_TYPE;

    case ChartDataKey.INVESTMENT_STATUS:
      return ChartTitle.INVESTMENT_STATUS;

    case ChartDataKey.FUND_INVESTMENT_NAV:
      return ChartTitle.FUND_INVESTMENT_NAV;

    case ChartDataKey.PUBLIC_OR_PRIVATE:
      return ChartTitle.PUBLIC_OR_PRIVATE;

    case ChartDataKey.COUNTRY:
      return ChartTitle.COUNTRY;

    case ChartDataKey.ASSET_CLASS:
      return ChartTitle.ASSET_CLASS;

    case ChartDataKey.SUB_ASSET_CLASS:
      return ChartTitle.SUB_ASSET_CLASS;

    case ChartDataKey.SECTOR:
      return ChartTitle.SECTOR;

    case ChartDataKey.INDUSTRY_GROUP:
      return ChartTitle.INDUSTRY_GROUP;

    case ChartDataKey.INDUSTRY:
      return ChartTitle.INDUSTRY;

    case ChartDataKey.SUB_INDUSTRY:
      return ChartTitle.SUB_INDUSTRY;

    case ChartDataKey.NAV_AS_OF_DATE:
      return ChartTitle.NAV_AS_OF_DATE;

    default:
      return "";
  }
};

export const navigateByAssetType = (assetType: AssetType) => {
  switch (assetType) {
    case AssetType.PreqinFund:
      router.navigate(URLs.PROTECTED.DASHBOARD + URLs.PROTECTED.ALT_CUSTODY + AltCustodyAssetPages.InvestmentDetails);
      break;

    case AssetType.Fund:
      router.navigate(URLs.PROTECTED.DASHBOARD + URLs.PROTECTED.ALT_CUSTODY + AltCustodyAssetPages.AddManually);
      break;

    default:
      router.navigate(
        URLs.PROTECTED.DASHBOARD + URLs.PROTECTED.ALT_CUSTODY + AltCustodyAssetPages.SelectAssets + "?tab=1",
      );
  }
};

export const navigateToMyAssets = (isAdvisor: boolean) => {
  isAdvisor ? router.navigate(URLs.PROTECTED.MY_CLIENTS_ASSETS) : router.navigate(URLs.PROTECTED.MY_ALT_CUSTODY_ASSETS);
};
